import React, { useState, useEffect } from 'react';
import { Timestamp } from 'firebase/firestore';

interface CountdownProps {
  endDate: string | Timestamp;
  format?: 'days' | 'hours' | 'minutes' | 'full';
  prefix?: string;
  suffix?: string;
  className?: string;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const Countdown: React.FC<CountdownProps> = ({ 
  endDate, 
  format = 'full', 
  prefix = '', 
  suffix = '',
  className = ''
}) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      // Handle both string dates and Firebase Timestamps
      let endDateTime: Date;
      
      if (endDate instanceof Timestamp) {
        endDateTime = endDate.toDate();
      } else {
        endDateTime = new Date(endDate);
      }
      
      const currentTime = new Date();
      
      // Check if the date is valid first, before any operations
      if (isNaN(endDateTime.getTime())) {
        console.error('Invalid end date provided to countdown:', endDate);
        setIsExpired(true);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      
      const difference = endDateTime.getTime() - currentTime.getTime();
      
      if (difference <= 0) {
        setIsExpired(true);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setIsExpired(false);
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    };

    // Calculate immediately
    calculateTimeLeft();

    // Update every second
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [endDate]);

  const formatTime = () => {
    if (isExpired) {
      return 'Expired';
    }

    const { days, hours, minutes, seconds } = timeLeft;

    switch (format) {
      case 'days':
        return `${days}d`;
      case 'hours':
        const totalHours = days * 24 + hours;
        return `${totalHours}h`;
      case 'minutes':
        const totalMinutes = days * 24 * 60 + hours * 60 + minutes;
        return `${totalMinutes}m`;
      case 'full':
      default:
        if (days > 0) {
          return `${days}d ${hours}h ${minutes}m ${seconds}s`;
        } else if (hours > 0) {
          return `${hours}h ${minutes}m ${seconds}s`;
        } else if (minutes > 0) {
          return `${minutes}m ${seconds}s`;
        } else {
          return `${seconds}s`;
        }
    }
  };

  if (isExpired && format !== 'full') {
    return null; // Don't render if expired and not full format
  }

  return (
    <span className={className}>
      {prefix && `${prefix} `}
      <span className="font-mono font-semibold">
        {formatTime()}
      </span>
      {suffix && ` ${suffix}`}
    </span>
  );
};

export default Countdown; 
